import { useEffect, useRef, useState } from 'react'
import { MoreHorizontal } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { MenuSurface } from '@/components/ui/menu-surface'
import { cn } from '@/lib/utils'

export interface PersonAction {
  label: string
  icon: LucideIcon
  onSelect: () => void
  /** Why it can't be used right now; the item shows it and stays inert. */
  disabledReason?: string
  destructive?: boolean
}

export function PersonActionsMenu({ name, actions }: { name: string; actions: PersonAction[] }) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={`More actions for ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'grid h-11 w-11 place-items-center rounded-[10px] text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]',
          open && 'bg-[var(--fl-surface-subtle)]'
        )}
      >
        <MoreHorizontal size={20} />
      </button>
      {open && (
        <MenuSurface
          ref={menuRef}
          role="menu"
          className="absolute right-0 z-40 mt-1 w-60 py-1.5"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false)
              triggerRef.current?.focus()
            }
          }}
        >
          {actions.map(({ label, icon: Icon, onSelect, disabledReason, destructive }) => (
            <button
              key={label}
              type="button"
              role="menuitem"
              aria-disabled={disabledReason ? true : undefined}
              onClick={() => {
                if (disabledReason) return
                setOpen(false)
                onSelect()
              }}
              className={cn(
                'flex min-h-11 w-full flex-col justify-center px-3.5 py-1.5 text-left',
                disabledReason
                  ? 'cursor-not-allowed text-[var(--fl-text-3)]'
                  : destructive
                    ? 'text-[var(--fl-error)] hover:bg-[var(--fl-surface-hover)]'
                    : 'text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)]'
              )}
            >
              <span className="flex items-center gap-2.5 text-sm">
                <Icon size={16} aria-hidden="true" />
                {label}
              </span>
              {disabledReason && <span className="ml-[26px] text-[11px]">{disabledReason}</span>}
            </button>
          ))}
        </MenuSurface>
      )}
    </div>
  )
}
